import React from 'react';
import { motion } from 'framer-motion';
import { journals } from '@/data/content';

const fadeInUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] as const },
};

const formatDate = (date: string) => {
    try {
        return new Intl.DateTimeFormat('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        }).format(new Date(`${date}T00:00:00`));
    } catch {
        return date;
    }
};

const JournalSection: React.FC = () => {
    if (!journals.length) {
        return null;
    }

    const [featured] = journals;

    return (
        <motion.section {...fadeInUp} className="py-16 md:py-24 bg-white dark:bg-gray-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl mb-10 md:mb-12">
                    <span className="inline-block text-sm font-semibold tracking-[0.18em] uppercase text-app-primary mb-3">
                        On parle de nous
                    </span>
                    <h2 className="text-3xl md:text-4xl font-black text-gray-900 dark:text-white mb-4">
                        Revue de presse
                    </h2>
                    <p className="text-base md:text-lg text-gray-600 dark:text-gray-300 leading-7">
                        Retrouvez les articles et parutions qui parlent de Bivin&apos;s Kitchen.
                    </p>
                </div>

                <article className="grid lg:grid-cols-[280px_minmax(0,1fr)] gap-6 lg:gap-8 rounded-3xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 overflow-hidden">
                    <div className="bg-white dark:bg-gray-900 p-6 flex items-center justify-center border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-800">
                        {featured.image_url ? (
                            <img
                                src={featured.image_url}
                                alt={featured.author || featured.title}
                                className="max-h-24 md:max-h-28 w-auto object-contain"
                            />
                        ) : null}
                    </div>

                    <div className="p-6 md:p-8">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500 dark:text-gray-400 mb-4">
                            <span>{formatDate(featured.published_at)}</span>
                            {featured.author ? <span>{featured.author}</span> : null}
                        </div>

                        <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white leading-tight mb-4">
                            {featured.title}
                        </h3>

                        <p className="text-base text-gray-600 dark:text-gray-300 leading-7 mb-6">
                            {featured.excerpt || featured.content}
                        </p>

                        <a
                            href={featured.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-app-primary font-semibold hover:underline"
                        >
                            Lire l&apos;article
                            <span aria-hidden="true">→</span>
                        </a>
                    </div>
                </article>
            </div>
        </motion.section>
    );
};

export default JournalSection;
